/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * useGlobalSearch — drives the Ctrl+K global search.
 *
 * Supports:
 * - Pages: instant local filter against navSections
 * - Data:  parallel React Query calls to existing list endpoints (size=5)
 *          with 300ms debounce on the search term
 * - Filter syntax: entity:status:search
 *   Examples: invoices:PAID, factures:payée:client, commandes:CONFIRMED
 */

import { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// VENTA services
import { getPartners } from '@/modules/venta/services/partner.service';
import { getInvoices } from '@/modules/venta/services/invoice.service';
import { getProducts } from '@/modules/venta/services/product.service';
import { getQuotations } from '@/modules/venta/services/quotation.service';
import { getSalesOrders } from '@/modules/venta/services/sales-order.service';
import { getPurchaseOrders } from '@/modules/venta/services/purchase-order.service';
import { dealService } from '@/modules/venta/services/deals.service';

import type { LucideIcon } from 'lucide-react';
import { useHasFeature } from '@/hooks/api/useModuleFeatures';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface SearchResultItem {
  id: string;
  category: SearchCategory;
  title: string;
  subtitle: string;
  meta?: string;
  metaVariant?: 'default' | 'success' | 'warning' | 'danger';
  href: string;
  icon?: LucideIcon;
}

export type SearchCategory =
  | 'pages'
  | 'partners'
  | 'invoices'
  | 'products'
  | 'quotations'
  | 'salesOrders'
  | 'purchaseOrders'
  | 'deals';

export interface FilterSuggestion {
  id: string;
  value: string;     // API enum value (e.g. 'PAID')
  label: string;     // Translated label (e.g. 'Payée')
  fillQuery: string; // What to set in the input (e.g. 'factures:PAID:')
}

export interface ActiveFilter {
  entityLabel: string;
  statusLabel: string | null;
}

interface NavItem {
  labelKey: string;
  href: string;
  icon: LucideIcon;
  feature?: string;
}

interface NavSection {
  id: string;
  titleKey: string;
  items: NavItem[];
}

interface UseGlobalSearchOptions {
  navSections: NavSection[];
}

/* ------------------------------------------------------------------ */
/*  Filter syntax configuration                                        */
/* ------------------------------------------------------------------ */

interface FilterEntityConfig {
  category: SearchCategory;
  aliases: string[];
  statuses: string[];
  statusI18nKey: (status: string) => string;
}

const FILTER_ENTITIES: FilterEntityConfig[] = [
  {
    category: 'invoices',
    aliases: ['invoices', 'invoice', 'factures', 'facture', 'inv', 'fact'],
    statuses: ['DRAFT', 'SENT', 'PARTIALLY_PAID', 'PAID', 'OVERDUE', 'CANCELLED', 'VOID'],
    statusI18nKey: (s) => `venta.invoices.status.${s}`,
  },
  {
    category: 'quotations',
    aliases: ['quotations', 'quotation', 'devis', 'quot'],
    statuses: ['DRAFT', 'SENT', 'ACCEPTED', 'REJECTED', 'EXPIRED', 'CONVERTED'],
    statusI18nKey: (s) => `venta.quotations.statuses.${s.toLowerCase()}`,
  },
  {
    category: 'salesOrders',
    aliases: ['salesorders', 'sales orders', 'sales-orders', 'commandes', 'commande'],
    statuses: ['DRAFT', 'CONFIRMED', 'FULFILLED', 'CANCELLED'],
    statusI18nKey: (s) => `venta.salesOrders.status.${s.toLowerCase()}`,
  },
  {
    category: 'purchaseOrders',
    aliases: ['purchaseorders', 'purchase orders', 'purchase-orders', 'achats', 'achat'],
    statuses: ['DRAFT', 'CONFIRMED', 'PARTIAL', 'RECEIVED', 'PAID', 'CANCELLED'],
    statusI18nKey: (s) => `venta.purchaseOrders.status.${s}`,
  },
  {
    category: 'partners',
    aliases: ['partners', 'partner', 'clients', 'client', 'customers', 'customer', 'suppliers', 'supplier', 'fournisseurs', 'fournisseur'],
    statuses: ['ACTIVE', 'INACTIVE'],
    statusI18nKey: (s) => `venta.suppliers.status.${s.toLowerCase()}`,
  },
  {
    category: 'deals',
    aliases: ['deals', 'deal', 'affaires', 'affaire', 'pipeline', 'opportunities', 'opportunity'],
    statuses: [],
    statusI18nKey: () => '',
  },
];

/* ------------------------------------------------------------------ */
/*  Filter query parser                                                */
/* ------------------------------------------------------------------ */

interface ParsedFilter {
  filterEntity: FilterEntityConfig | null;
  filterStatus: string | null;
  search: string;
  showSuggestions: boolean;
  statusPartial: string;
  entityAlias: string;
}

function resolveStatus(
  config: FilterEntityConfig,
  typed: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any,
): string | null {
  if (!typed) return null;
  const typedLower = typed.toLowerCase();

  // Exact enum match (case-insensitive)
  const enumMatch = config.statuses.find((s) => s.toLowerCase() === typedLower);
  if (enumMatch) return enumMatch;

  // Translated label match (case-insensitive)
  const labelMatch = config.statuses.find(
    (s) => t(config.statusI18nKey(s)).toLowerCase() === typedLower,
  );
  if (labelMatch) return labelMatch;

  return null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseFilterQuery(raw: string, t: any): ParsedFilter {
  const none: ParsedFilter = {
    filterEntity: null, filterStatus: null, search: raw,
    showSuggestions: false, statusPartial: '', entityAlias: '',
  };

  const colonIndex = raw.indexOf(':');
  if (colonIndex === -1) return none;

  const entityAlias = raw.substring(0, colonIndex).trim().toLowerCase();
  const config = FILTER_ENTITIES.find((e) => e.aliases.includes(entityAlias));
  if (!config) return none;

  const afterColon = raw.substring(colonIndex + 1);
  const secondColonIndex = afterColon.indexOf(':');

  let statusPart: string;
  let searchPart: string;

  if (secondColonIndex === -1) {
    statusPart = afterColon.trim();
    searchPart = '';
  } else {
    statusPart = afterColon.substring(0, secondColonIndex).trim();
    searchPart = afterColon.substring(secondColonIndex + 1).trim();
  }

  // "entity:" with nothing after → show all suggestions
  if (statusPart === '' && secondColonIndex === -1) {
    return {
      filterEntity: config, filterStatus: null, search: '',
      showSuggestions: true, statusPartial: '', entityAlias,
    };
  }

  // Try resolving the status
  const matched = resolveStatus(config, statusPart, t);

  if (matched) {
    return {
      filterEntity: config, filterStatus: matched, search: searchPart,
      showSuggestions: false, statusPartial: statusPart, entityAlias,
    };
  }

  // Partial status → show filtered suggestions
  return {
    filterEntity: config, filterStatus: null, search: '',
    showSuggestions: true, statusPartial: statusPart, entityAlias,
  };
}

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

export function useGlobalSearch({ navSections }: UseGlobalSearchOptions) {
  const { t } = useTranslation(['venta', 'core']);
  const location = useLocation();
  const isVenta = location.pathname.startsWith('/venta');
  const { hasFeature } = useHasFeature();

  const [query, setQuery] = useState('');

  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  // -- Parse filter syntax (instant for UI, debounced for API) --
  const instantParsed = useMemo(() => parseFilterQuery(query, t), [query, t]);
  const debouncedParsed = useMemo(() => parseFilterQuery(debouncedQuery, t), [debouncedQuery, t]);

  const isFilterMode = debouncedParsed.filterEntity !== null;
  const filterCategory = debouncedParsed.filterEntity?.category ?? null;
  const filterStatus = debouncedParsed.filterStatus;
  const apiSearch = isFilterMode ? debouncedParsed.search : debouncedQuery.trim();

  const apiEnabled = isVenta && (
    (!isFilterMode && apiSearch.length >= 2) ||
    (isFilterMode && filterStatus !== null)
  );

  // -- Filter suggestions (instant, local) --
  const filterSuggestions = useMemo<FilterSuggestion[]>(() => {
    if (!instantParsed.showSuggestions || !instantParsed.filterEntity) return [];

    const config = instantParsed.filterEntity;
    const partial = instantParsed.statusPartial.toLowerCase();

    return config.statuses
      .map((s) => ({
        id: `suggestion-${s}`,
        value: s,
        label: t(config.statusI18nKey(s)),
        fillQuery: `${instantParsed.entityAlias}:${s}:`,
      }))
      .filter((s) => {
        if (!partial) return true;
        return (
          s.label.toLowerCase().includes(partial) ||
          s.value.toLowerCase().includes(partial)
        );
      });
  }, [instantParsed, t]);

  // -- Active filter display info --
  const activeFilter = useMemo<ActiveFilter | null>(() => {
    if (!instantParsed.filterEntity) return null;
    const config = instantParsed.filterEntity;
    return {
      entityLabel: t(`globalSearch.${config.category}`, { ns: 'common' }),
      statusLabel: instantParsed.filterStatus
        ? t(config.statusI18nKey(instantParsed.filterStatus))
        : null,
    };
  }, [instantParsed, t]);

  // -- Page search (instant, local — disabled in filter mode) --
  const pageResults = useMemo<SearchResultItem[]>(() => {
    if (instantParsed.filterEntity) return [];
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const items: SearchResultItem[] = [];
    navSections.forEach((section) => {
      section.items.forEach((item) => {
        if ((item as { disabled?: boolean }).disabled) return;
        if (item.feature && !hasFeature('VENTA', item.feature)) return;
        const label = t(item.labelKey);
        const sectionTitle = t(section.titleKey);
        const modulePrefix = item.href.startsWith('/superadmin') ? 'SuperAdmin' : 'VENTA';
        if (
          label.toLowerCase().includes(q) ||
          sectionTitle.toLowerCase().includes(q)
        ) {
          items.push({
            id: `page-${item.href}`,
            category: 'pages',
            title: label,
            subtitle: `${modulePrefix} · ${sectionTitle}`,
            href: item.href,
            icon: item.icon,
          });
        }
      });
    });
    return items;
  }, [query, navSections, t, instantParsed.filterEntity, hasFeature]);

  // -- Data queries (parallel, debounced) --
  // In filter mode only the targeted entity fires; others are disabled.

  const partners = useQuery({
    queryKey: ['globalSearch', 'partners', apiSearch, filterStatus],
    queryFn: () => getPartners({ search: apiSearch || undefined, size: 5 }),
    enabled: apiEnabled && (!isFilterMode || filterCategory === 'partners'),
    staleTime: 30_000,
  });

  const invoices = useQuery({
    queryKey: ['globalSearch', 'invoices', apiSearch, filterStatus],
    queryFn: () => getInvoices({ search: apiSearch || undefined, status: filterStatus || undefined, size: 5 }),
    enabled: apiEnabled && (!isFilterMode || filterCategory === 'invoices'),
    staleTime: 30_000,
  });

  const products = useQuery({
    queryKey: ['globalSearch', 'products', apiSearch, filterStatus],
    queryFn: () => getProducts({ search: apiSearch || undefined, status: filterStatus || undefined, size: 5 }),
    enabled: apiEnabled && (!isFilterMode || filterCategory === 'products'),
    staleTime: 30_000,
  });

  const quotations = useQuery({
    queryKey: ['globalSearch', 'quotations', apiSearch, filterStatus],
    queryFn: () => getQuotations({ search: apiSearch || undefined, status: filterStatus || undefined, size: 5 }),
    enabled: apiEnabled && (!isFilterMode || filterCategory === 'quotations'),
    staleTime: 30_000,
  });

  const salesOrders = useQuery({
    queryKey: ['globalSearch', 'salesOrders', apiSearch, filterStatus],
    queryFn: () => getSalesOrders({ search: apiSearch || undefined, status: filterStatus || undefined, size: 5 }),
    enabled: apiEnabled && (!isFilterMode || filterCategory === 'salesOrders'),
    staleTime: 30_000,
  });


  const purchaseOrders = useQuery({
    queryKey: ['globalSearch', 'purchaseOrders', apiSearch, filterStatus],
    queryFn: () => getPurchaseOrders({ search: apiSearch || undefined, status: filterStatus || undefined, size: 5 }),
    enabled: apiEnabled && (!isFilterMode || filterCategory === 'purchaseOrders'),
    staleTime: 30_000,
  });

  const deals = useQuery({
    queryKey: ['globalSearch', 'deals', apiSearch],
    queryFn: () => dealService.getDeals({ search: apiSearch || undefined, size: 5 }),
    enabled: apiEnabled && (!isFilterMode || filterCategory === 'deals'),
    staleTime: 30_000,
  });

  // -- Map API results to SearchResultItem[] --

  const dataResults = useMemo<SearchResultItem[]>(() => {
    if (!apiEnabled) return [];
    const items: SearchResultItem[] = [];

    (partners.data?.content ?? []).forEach((p) => {
      items.push({
        id: `partner-${p.id}`,
        category: 'partners',
        title: p.name,
        subtitle: p.email || p.role,
        href: `/venta/partners/${p.id}`,
      });
    });

    (invoices.data?.content ?? []).forEach((inv) => {
      items.push({
        id: `invoice-${inv.id}`,
        category: 'invoices',
        title: inv.invoiceNumber,
        subtitle: `${inv.customerName} \u00b7 ${inv.currency} ${inv.totalAmount.toLocaleString()}`,
        meta: t(`venta.invoices.status.${inv.status}`),
        metaVariant: inv.status === 'PAID' ? 'success'
          : inv.status === 'OVERDUE' ? 'danger'
          : inv.status === 'SENT' ? 'warning' : 'default',
        href: `/venta/invoices/${inv.id}`,
      });
    });

    (products.data?.content ?? []).forEach((p) => {
      items.push({
        id: `product-${p.id}`,
        category: 'products',
        title: p.name,
        subtitle: p.sku ? `SKU: ${p.sku}` : (p.categoryName ?? ''),
        meta: `${t('venta.products.colStock')}: ${p.totalStock}`,
        href: `/venta/products/${p.id}`,
      });
    });

    (quotations.data?.content ?? []).forEach((q) => {
      items.push({
        id: `quotation-${q.id}`,
        category: 'quotations',
        title: q.quotationNumber,
        subtitle: `${q.customerName} \u00b7 ${q.currency} ${q.totalAmount.toLocaleString()}`,
        meta: t(`venta.quotations.statuses.${q.status.toLowerCase()}`),
        metaVariant: q.status === 'ACCEPTED' ? 'success'
          : q.status === 'EXPIRED' ? 'danger' : 'default',
        href: `/venta/quotations/${q.id}`,
      });
    });

    (salesOrders.data?.content ?? []).forEach((so) => {
      items.push({
        id: `salesOrder-${so.id}`,
        category: 'salesOrders',
        title: so.orderNumber,
        subtitle: `${so.customerName} \u00b7 ${so.currency} ${so.totalAmount.toLocaleString()}`,
        meta: t(`venta.salesOrders.status.${so.status.toLowerCase()}`),
        href: `/venta/sales-orders/${so.id}`,
      });
    });


    (purchaseOrders.data?.content ?? []).forEach((po) => {
      items.push({
        id: `purchaseOrder-${po.id}`,
        category: 'purchaseOrders',
        title: po.poNumber,
        subtitle: `${po.supplierName} \u00b7 ${po.currency} ${po.totalAmount.toLocaleString()}`,
        meta: t(`venta.purchaseOrders.status.${po.status}`),
        href: `/venta/purchase-orders/${po.id}`,
      });
    });

    (deals.data?.content ?? []).forEach((d) => {
      items.push({
        id: `deal-${d.id}`,
        category: 'deals',
        title: d.title,
        subtitle: `${d.partnerName || ''} \u00b7 ${d.currency} ${d.value.toLocaleString()}`,
        meta: d.stageName,
        metaVariant: d.stageName === 'Won' ? 'success' : d.stageName === 'Lost' ? 'danger' : 'default',
        href: `/venta/deals/${d.id}`,
      });
    });

    return items;
  }, [apiEnabled, partners.data, invoices.data, products.data, quotations.data, salesOrders.data, purchaseOrders.data, t]);

  const allResults = useMemo(
    () => [...dataResults, ...pageResults],
    [dataResults, pageResults],
  );

  const isLoading = apiEnabled && (
    partners.isLoading || invoices.isLoading || products.isLoading ||
    quotations.isLoading || salesOrders.isLoading || purchaseOrders.isLoading
  );

  return {
    query,
    setQuery,
    results: allResults,
    pageResults,
    dataResults,
    isLoading,
    isVenta,
    filterSuggestions,
    activeFilter,
  };
}
